/**
 * Hook para gerenciar seleção de serviços
 */

import { useState, useEffect, useMemo } from "react";
import type { PublicService } from "../types";
import { getPublicServices } from "../../../services/public";
import { useFeedbackState } from "../../../components/ui/ToastProvider";

interface UseServiceSelectionProps {
  slug: string | null;
  allowMultiServices?: boolean;
  date?: string;
  selectedProviderId?: string;
}

export function useServiceSelection({
  slug,
  allowMultiServices,
  date,
  selectedProviderId,
}: UseServiceSelectionProps) {
  const [services, setServices] = useState<PublicService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useFeedbackState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);

  useEffect(() => {
    if (!slug) {
      setServices([]);
      setError(null);
      return;
    }

    const fetchServices = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log("🔄 Buscando serviços para slug:", slug);
        const list = await getPublicServices(slug, {
          date,
          providerId: selectedProviderId || undefined,
        });
        console.log("✅ Serviços recebidos:", list);

        // Garantir que list é um array
        const normalizedList = Array.isArray(list) ? list : [];
        setServices(normalizedList);

        const availableServiceIds = new Set(
          normalizedList.map((service) => String(service.id || "")).filter(Boolean)
        );
        setSelectedServiceId((previous) =>
          previous && availableServiceIds.has(previous) ? previous : ""
        );
        setSelectedServiceIds((previous) =>
          previous.filter((id) => availableServiceIds.has(id))
        );

        // Se não houver serviços, definir um erro amigável
        if (normalizedList.length === 0) {
          setError("Nenhum serviço encontrado para este estabelecimento");
        }
      } catch (err) {
        console.error("❌ Erro ao buscar serviços:", err);
        setServices([]);
        setError(err instanceof Error ? err.message : "Erro ao carregar serviços");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [slug, date, selectedProviderId]);

  const selectedService = useMemo(
    () => services.find((s) => s.id === selectedServiceId),
    [services, selectedServiceId]
  );

  const selectedServices = useMemo(
    () => services.filter((s) => selectedServiceIds.includes(s.id)),
    [services, selectedServiceIds]
  );

  const multiEnabled = Boolean(allowMultiServices);

  const toggleService = (serviceId: string) => {
    if (multiEnabled) {
      setSelectedServiceIds((prev) => {
        const exists = prev.includes(serviceId);
        const next = exists ? prev.filter((x) => x !== serviceId) : [...prev, serviceId];

        if (!exists && next.length === 1) setSelectedServiceId(serviceId);
        if (exists && next.length === 0) setSelectedServiceId("");

        return next;
      });
    } else {
      setSelectedServiceId(serviceId);
    }
  };

  const isSelected = (serviceId: string) => {
    return multiEnabled
      ? selectedServiceIds.includes(serviceId)
      : serviceId === selectedServiceId;
  };

  return {
    services,
    loading,
    error,
    selectedServiceId,
    selectedServiceIds,
    selectedService,
    selectedServices,
    multiEnabled,
    toggleService,
    isSelected,
    setSelectedServiceId,
    setSelectedServiceIds,
  };
}
